'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Bindet ein controlled <input> an einen extern verwalteten Wert (z. B. einen
 * URL-Search-Param), ohne dass jeder Tastendruck den Parent re-rendert.
 *
 * - Tastatureingaben aktualisieren ausschliesslich den lokalen State → Input
 *   bleibt responsiv, auch wenn der Parent (z. B. Apollo-Query + router.replace)
 *   teurer rendert.
 * - Nach `delay` ms ohne weitere Eingabe wird `onDebouncedChange` aufgerufen,
 *   sodass der externe Zustand (URL, Apollo-Variables) synchronisiert wird.
 * - Aendert sich der externe Wert von ausserhalb (z. B. clearFilters, Browser-
 *   Navigation), wird der lokale Wert sofort uebernommen und ein noch laufender
 *   Debounce-Timer verworfen.
 * - `commit()` erlaubt Gesten (z. B. ✕-Button, Escape), die sofort aendern muessen:
 *   setzt lokal, verwirft den Timer und meldet sofort nach aussen.
 *
 * @example
 * const [searchInput, setSearchInput, commit] = useDebouncedInput(
 *   filters.search,
 *   (value) => onFilterChange({ ...filters, search: value }),
 *   300,
 * );
 * // <input value={searchInput} onChange={(e) => setSearchInput(e.target.value)} />
 * // <button onClick={() => commit('')}>✕</button>
 */
export function useDebouncedInput(
  externalValue: string,
  onDebouncedChange: (value: string) => void,
  delay = 300,
): readonly [string, (value: string) => void, (value: string) => void] {
  const [localValue, setLocalValue] = useState(externalValue);
  const lastSyncedRef = useRef(externalValue);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const callbackRef = useRef(onDebouncedChange);

  useEffect(() => {
    callbackRef.current = onDebouncedChange;
  }, [onDebouncedChange]);

  // Externer Wert hat sich geaendert (Reset, Navigation) → lokal uebernehmen
  // und einen evtl. laufenden Debounce-Timer verwerfen.
  useEffect(() => {
    if (externalValue !== lastSyncedRef.current) {
      lastSyncedRef.current = externalValue;
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      setLocalValue(externalValue);
    }
  }, [externalValue]);

  // Lokale Aenderung debounced nach aussen propagieren
  useEffect(() => {
    if (localValue === lastSyncedRef.current) return;
    timerRef.current = setTimeout(() => {
      lastSyncedRef.current = localValue;
      timerRef.current = null;
      callbackRef.current(localValue);
    }, delay);
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [localValue, delay]);

  // Sofort-Pfad fuer Gesten, die keine Verzoegerung vertragen (✕, Escape):
  // setzt lokal, verwirft einen laufenden Timer und meldet sofort nach aussen.
  const commit = useCallback((value: string) => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    lastSyncedRef.current = value;
    setLocalValue(value);
    callbackRef.current(value);
  }, []);

  return [localValue, setLocalValue, commit] as const;
}
